// src/component/AdminMusicMonitoring.js
// ★ 音楽監視（設定）親コンポーネント
// Firestore: appFlags/perf を編集して TopPageMusicSection の lag watch を制御
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Switch,
  FormControlLabel,
  Divider,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate } from "react-router-dom";

import { AppThemeContext } from "./AppShell";
import { useAuth } from "../context/auth";
import { db } from "../api/firebase";
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";

const DEFAULTS = {
  lagWatchEnabled: false,
  lagWatchAdminOnly: true,
  lagWarnThresholdMs: 200,
  lagSampleIntervalMs: 50,
};

export default function AdminMusicMonitoring() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFF";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";

  const { user } = useAuth();

  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingAdmin, setLoadingAdmin] = useState(true);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(DEFAULTS);
  const [error, setError] = useState("");

  const flagsRef = useMemo(() => doc(db, "appFlags", "perf"), []);

  // 管理者チェック（users/{uid}.isAdmin）
  useEffect(() => {
    const run = async () => {
      if (!user?.uid) {
        setIsAdmin(false);
        setLoadingAdmin(false);
        return;
      }
      try {
        const uref = doc(db, "users", user.uid);
        const usnap = await getDoc(uref);
        setIsAdmin(Boolean(usnap.exists() ? usnap.data()?.isAdmin : false));
      } catch (e) {
        console.warn("Admin check failed:", e);
        setIsAdmin(false);
      } finally {
        setLoadingAdmin(false);
      }
    };
    run();
  }, [user?.uid]);

  // フラグ購読
  useEffect(() => {
    const unsub = onSnapshot(
      flagsRef,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setState({
          lagWatchEnabled: Boolean(data.lagWatchEnabled),
          lagWatchAdminOnly: data.lagWatchAdminOnly !== false,
          lagWarnThresholdMs: Number(data.lagWarnThresholdMs) || DEFAULTS.lagWarnThresholdMs,
          lagSampleIntervalMs: Number(data.lagSampleIntervalMs) || DEFAULTS.lagSampleIntervalMs,
        });
        setLoading(false);
      },
      (err) => {
        console.warn("flags onSnapshot error:", err);
        setError("設定の読み込みに失敗しました。権限と通信状況をご確認ください。");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [flagsRef]);

  const updateState = (patch) => setState((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setError("");
    if (!isAdmin) {
      setError("権限がありません（管理者のみ操作できます）。");
      return;
    }

    const threshold = Math.max(50, Math.min(5000, Number(state.lagWarnThresholdMs) || 200));
    const sample = Math.max(10, Math.min(1000, Number(state.lagSampleIntervalMs) || 50));

    setSaving(true);
    try {
      await setDoc(
        flagsRef,
        {
          lagWatchEnabled: Boolean(state.lagWatchEnabled),
          lagWatchAdminOnly: Boolean(state.lagWatchAdminOnly),
          lagWarnThresholdMs: threshold,
          lagSampleIntervalMs: sample,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        },
        { merge: true }
      );
    } catch (e) {
      console.error("Failed to save perf flags:", e);
      setError("保存に失敗しました。Firestoreの権限設定をご確認ください。");
    } finally {
      setSaving(false);
    }
  };

  if (loadingAdmin || loading) {
    return (
      <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(-1)}
            sx={{ borderRadius: 999 }}
          >
            戻る
          </Button>
        </Stack>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: textBase, opacity: 0.8 }}>
              読み込み中…
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, maxWidth: 920, mx: "auto" }}>
      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate(-1)}
          sx={{ borderRadius: 999 }}
        >
          戻る
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          onClick={save}
          disabled={!isAdmin || saving}
          sx={{
            borderRadius: 999,
            bgcolor: accent,
            "&:hover": { bgcolor: accent },
          }}
        >
          保存
        </Button>
      </Stack>

      <Typography
        variant="h6"
        sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase, mb: 0.5 }}
      >
        音楽監視（設定）
      </Typography>

      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 2 }}>
        TopPageMusicSection のパフォーマンス監視（LAGログ）を Firestore で切り替えます。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!isAdmin && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          この画面は管理者のみ操作できます。
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.2, sm: 2.6 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(state.lagWatchEnabled)}
                  onChange={(e) => updateState({ lagWatchEnabled: e.target.checked })}
                />
              }
              label="LAG監視を有効にする（console.warn）"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(state.lagWatchAdminOnly)}
                  onChange={(e) => updateState({ lagWatchAdminOnly: e.target.checked })}
                />
              }
              label="管理者のみ監視（推奨）"
            />
          </Stack>

          <Divider />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              label="警告閾値（ms）"
              value={state.lagWarnThresholdMs}
              onChange={(e) => updateState({ lagWarnThresholdMs: e.target.value })}
              size="small"
              sx={{ width: { xs: "100%", sm: 220 } }}
              helperText="このms以上止まったら [LAG] を出します（例:200）"
            />
            <TextField
              label="サンプル間隔（ms）"
              value={state.lagSampleIntervalMs}
              onChange={(e) => updateState({ lagSampleIntervalMs: e.target.value })}
              size="small"
              sx={{ width: { xs: "100%", sm: 220 } }}
              helperText="50ms推奨。小さすぎると監視自体が負荷になります。"
            />
          </Stack>

          <Alert severity="info">
            反映はリアルタイムです。OFFにするとユーザー側の interval は即停止し、再読込は不要です。
          </Alert>
        </Stack>
      </Paper>
    </Box>
  );
}
